import type { ExecutionOutcome } from './executions'

/**
 * Cancelling a running ETL run (D6b(ii)): `EtlRunCancellationController` (Lighthouse.Api
 * Contracts/Etl/EtlRunCancellationContracts.cs), which is the source of truth. camelCase, as the API serialises;
 * every C# nullable is `| null`.
 *
 * THE D14 REQUEST-ROW PATTERN. The API records the request; Automaton's `EtlRunCancelExecutor` finds the Hangfire
 * perform holding the run (not the run's creating job, which a resume keeps) and moves it to Deleted, which Hangfire
 * turns into the job's cancellation token. So a 202 means "recorded", never "cancelled": follow the request's
 * `status`, and the run's own `runOutcome`, to see the run stop.
 */

/**
 * A cancel request's status, by name. `Requested` and `Delivered` are OPEN (at most one per run); the other five
 * are decisions and carry `decidedAt`.
 *
 * - `Completed`: the run stopped for this request — it is recorded Cancelled.
 * - `FinishedBeforeStop`: the stop was delivered, but the run ended another way first (its last batch merged, it
 *   failed, it halted — `runStatusAtDecision` says which). NOTHING WAS STOPPED by this request.
 * - `AlreadyFinished`: it had ended before anything was delivered — nothing was cancelled.
 * - `ClosedNotRunning`: recorded Running, but no Hangfire job was performing it; Automaton closed the record.
 *   NOTHING WAS STOPPED — there was nothing running to stop.
 * - `Failed`: nothing could be delivered; `statusDetail` says why, and the Hangfire dashboard is the way out.
 */
export type EtlRunCancelStatus =
  | 'Requested'
  | 'Delivered'
  | 'Completed'
  | 'FinishedBeforeStop'
  | 'AlreadyFinished'
  | 'ClosedNotRunning'
  | 'Failed'

/** The two open statuses. */
export const OPEN_CANCEL_STATUSES: readonly EtlRunCancelStatus[] = ['Requested', 'Delivered']

/** One request to cancel a run, and what became of it (`EtlRunCancellationDto`). */
export interface EtlRunCancellation {
  requestId: string
  runId: string
  /** Copied when the request was written, so it survives the run being trimmed. */
  runKey: string
  /** By name. A value this build does not know is shown as itself, never guessed at (cancelFormat). */
  status: EtlRunCancelStatus
  /** Automaton's account of the decision, in its own words — never exception text. */
  statusDetail: string | null
  requestedAt: string
  /**
   * Who asked, as stamped at write time. Served only to a caller passing the administrator policy: for anyone else
   * null with `requestedByWithheld` true. Withheld is not absent.
   */
  requestedBy: string | null
  requestedByWithheld: boolean
  /** Why — required at request time. Administrator-only, as `requestedBy`. */
  note: string | null
  noteWithheld: boolean
  /** The run was stale by the worker's own rule when the request was written. */
  runWasStale: boolean
  deliveredAt: string | null
  /** The Hangfire jobs the stop was delivered to — not necessarily the run's creating job. Empty until delivered. */
  deliveredJobIds: string[]
  decidedAt: string | null
  /** The run's own status (an EtlRunStatus name) when the request was decided; null while open. */
  runStatusAtDecision: string | null
  /** The run's outcome NOW, by the worker's stale rule. Null when the run no longer exists. */
  runOutcome: ExecutionOutcome | null
  /**
   * True only on the POST that wrote this request. A POST that found one already open answers 200 with false:
   * nothing was written, and its note was not recorded.
   */
  acceptedByThisRequest: boolean
}

/** A run's cancel requests, newest first (`EtlRunCancellationListDto`). */
export interface EtlRunCancellationList {
  /**
   * False where `Etl.EtlRunCancelRequest` does not exist (migration AddSignalHandlingAndRunCancellation not
   * applied). Then `latest` and `items` are null — NOT MEASURED, never an empty list that reads as "nobody asked".
   */
  provisioned: boolean
  latest: EtlRunCancellation | null
  /** At most twenty, newest first; null exactly when `provisioned` is false. */
  items: EtlRunCancellation[] | null
}

/** The 409 body: the run is not running (`EtlRunNotRunningDto`). `status` is the run's EtlRunStatus name. */
export interface EtlRunNotRunning {
  title: string
  status: string
  detail: string
}

/** The server's bound on a note (`EtlRunCancelRequestEntityConfiguration.NoteLength`), after trimming. */
export const RUN_CANCEL_NOTE_MAX_LENGTH = 1000
